import { forwardRef, type ReactNode } from 'react';
import {
  Outlet,
  createLink,
  createRootRouteWithContext,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Anchor,
  AppShell,
  Avatar,
  Badge,
  Burger,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  NavLink,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
  type NavLinkProps,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconBrandGoogle,
  IconBrandWindows,
  IconBuildings,
  IconChevronDown,
  IconFlask,
  IconBuildingStore,
  IconInbox,
  IconLayoutDashboard,
  IconListDetails,
  IconLogout,
  IconMoonStars,
  IconRocket,
  IconSend,
  IconSignature,
  IconSun,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { isMockSession, isMspStaff, useSession, useSignInProviders } from '../auth/session';
import { useExitViewAsPartner } from '../api/resellers';
import { OnboardingState, useTenant } from '../api/tenants';
import { LogoMark } from '../components/LogoMark';
import { LanguageSelector } from '../components/LanguageSelector';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

/**
 * NavLink rendered as a plain anchor so `createLink` can wire the router into it with the route
 * generics intact — the old markup nested a Mantine NavLink inside a router Link, which produced
 * an anchor inside an anchor.
 */
const RouterNavLink = createLink(
  forwardRef<HTMLAnchorElement, Omit<NavLinkProps, 'href'> & { children?: ReactNode }>(
    function RouterNavLinkAnchor(props, ref) {
      return <NavLink component="a" ref={ref} {...props} />;
    },
  ),
);

function RootLayout() {
  const { t } = useTranslation(['common', 'nav']);
  const [opened, { toggle, close }] = useDisclosure();
  const { data: session, isPending, isError, error } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { tenantId } = useParams({ strict: false });

  if (isPending) {
    return (
      <Center h="100vh" style={{ background: 'radial-gradient(600px circle at 50% 30%, var(--portal-glow), transparent 70%)' }}>
        <Loader type="dots" />
      </Center>
    );
  }

  if (isError) {
    return (
      <CenteredShellScreen>
        <Title order={3} ta="center">
          {t('common:apiUnreachableTitle')}
        </Title>
        <Text c="dimmed" ta="center" size="sm">
          {error instanceof Error ? error.message : t('common:unknownError')}
        </Text>
        <Text c="dimmed" size="xs" ta="center">
          {t('common:apiUnreachableHint')}
        </Text>
        <Button mt="sm" onClick={() => window.location.reload()}>
          {t('common:retry')}
        </Button>
        <LanguageSelector compact />
      </CenteredShellScreen>
    );
  }

  if (!session) {
    return <SignInScreen />;
  }

  // A principal exists but nobody has granted it anything yet. This is the expected state on a
  // first sign-in, so it gets its own screen rather than a wall of empty tables or a permission error.
  // An administrator viewing as a partner with an empty book is different: they chose the overlay
  // and need the exit banner, not the awaiting-access screen. A real ResellerAdmin with an empty
  // book is the same shape — they must be able to add their first customer.
  if (
    session.roleAssignments.length === 0
    && !session.viewAsPartner
    && (session.partnerBooks?.length ?? 0) === 0
  ) {
    return <AwaitingAccessScreen email={session.email} />;
  }

  const msp = isMspStaff(session);
  const viewingAsPartner = session.viewAsPartner ?? null;
  const partnerScoped =
    Boolean(viewingAsPartner) || (session.partnerBooks?.length ?? 0) > 0;

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header
        style={{
          backdropFilter: 'blur(10px)',
          backgroundColor: 'color-mix(in srgb, var(--mantine-color-body) 86%, transparent)',
        }}
      >
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <LogoMark size={24} />
            <Text ff="'Fraunces Variable', Georgia, serif" fw={600} fz="md" lh={1}>
              {t('common:brand')}
            </Text>
            {isMockSession ? (
              <Tooltip label={t('common:sampleDataHint')}>
                <Badge size="xs" variant="light" color="orange">
                  {t('common:sampleData')}
                </Badge>
              </Tooltip>
            ) : null}
          </Group>
          <Group gap="xs" wrap="nowrap">
            <LanguageSelector compact />
            <ColorSchemeToggle />
            <UserMenu displayName={session.displayName} email={session.email} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" style={{ backgroundColor: 'var(--portal-ground)' }}>
        <Stack gap="lg">
          {msp ? (
            <Stack gap={4}>
              <NavSectionLabel>{t('nav:platform')}</NavSectionLabel>
              <RouterNavLink
                to="/tenants"
                label={t('nav:tenants')}
                leftSection={<IconBuildings size={16} stroke={1.75} />}
                active={pathname === '/tenants' || (pathname.startsWith('/tenants') && !tenantId)}
                onClick={close}
              />
              <RouterNavLink
                to="/resellers"
                label={t('nav:partners')}
                leftSection={<IconBuildingStore size={16} stroke={1.75} />}
                active={pathname.startsWith('/resellers')}
                onClick={close}
              />
              <RouterNavLink
                to="/enquiries"
                label={t('nav:enquiries')}
                leftSection={<IconInbox size={16} stroke={1.75} />}
                active={pathname.startsWith('/enquiries')}
                onClick={close}
              />
              <RouterNavLink
                to="/access"
                label={t('nav:teamAccess')}
                leftSection={<IconUsers size={16} stroke={1.75} />}
                active={pathname.startsWith('/access')}
                onClick={close}
              />
            </Stack>
          ) : (
            <Stack gap={4}>
              {partnerScoped ? (
                <>
                  <NavSectionLabel>{t('nav:partner')}</NavSectionLabel>
                  <RouterNavLink
                    to="/tenants"
                    label={t('nav:customers')}
                    leftSection={<IconBuildings size={16} stroke={1.75} />}
                    active={pathname === '/tenants' || (pathname.startsWith('/tenants') && !tenantId)}
                    onClick={close}
                  />
                  <RouterNavLink
                    to="/access"
                    label={t('nav:teamAccess')}
                    leftSection={<IconUsers size={16} stroke={1.75} />}
                    active={pathname.startsWith('/access')}
                    onClick={close}
                  />
                </>
              ) : null}
              <TenantAssignmentNav
                assignments={session.roleAssignments}
                pathname={pathname}
                onNavigate={close}
                partnerScoped={partnerScoped}
              />
            </Stack>
          )}

          {tenantId ? (
            <TenantContextNav tenantId={tenantId} pathname={pathname} onNavigate={close} />
          ) : null}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        {viewingAsPartner ? <ViewAsPartnerBanner partner={viewingAsPartner} /> : null}
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

function ViewAsPartnerBanner({ partner }: { partner: { id: string; name: string } }) {
  const { t } = useTranslation(['partners', 'common']);
  const exit = useExitViewAsPartner();

  return (
    <Alert
      color="orange"
      variant="light"
      mb="md"
      title={t('partners:viewAsBannerTitle', { name: partner.name })}
      withCloseButton={false}
    >
      <Group justify="space-between" align="center" gap="sm" wrap="wrap">
        <Text size="sm">{t('partners:viewAsBannerBody')}</Text>
        <Button
          size="compact-sm"
          variant="filled"
          color="orange"
          loading={exit.isPending}
          onClick={() =>
            exit.mutate(undefined, {
              onSuccess: () => window.location.assign('/resellers'),
              onError: (mutationError) =>
                notifications.show({
                  color: 'red',
                  message:
                    mutationError instanceof Error
                      ? mutationError.message
                      : t('partners:viewAsExitError'),
                }),
            })
          }
        >
          {t('partners:exitViewAs')}
        </Button>
      </Group>
    </Alert>
  );
}

function NavSectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      c="dimmed"
      fw={600}
      tt="uppercase"
      px="sm"
      style={{ fontSize: 11, letterSpacing: '0.06em' }}
    >
      {children}
    </Text>
  );
}

/**
 * The section that follows the operator into a tenant: every deep screen used to be reachable only
 * by typing its URL.
 */
function TenantContextNav({
  tenantId,
  pathname,
  onNavigate,
}: {
  tenantId: string;
  pathname: string;
  onNavigate: () => void;
}) {
  const { t } = useTranslation('nav');
  const { data: tenant, isPending } = useTenant(tenantId);
  const base = `/tenants/${tenantId}`;
  const params = { tenantId };
  // Incomplete onboarding is the primary action for that tenant — keep it above Overview so it
  // cannot be missed. Once org-wide (or offboarding), demote it back into the tools list.
  const onboardingIncomplete =
    tenant != null && tenant.onboardingState < OnboardingState.OrgWide;

  const onboardingLink = (
    <RouterNavLink
      to="/tenants/$tenantId/onboarding"
      params={params}
      label={t('onboarding')}
      leftSection={<IconRocket size={16} stroke={1.75} />}
      active={pathname === `${base}/onboarding`}
      onClick={onNavigate}
    />
  );

  return (
    <Stack gap={4}>
      {isPending ? (
        <Skeleton height={10} width={110} mx="sm" my={4} />
      ) : (
        <NavSectionLabel>{tenant?.name ?? t('tenant')}</NavSectionLabel>
      )}
      {onboardingIncomplete ? onboardingLink : null}
      <RouterNavLink
        to="/tenants/$tenantId"
        params={params}
        search={{ tab: 'settings' }}
        label={t('overview')}
        leftSection={<IconLayoutDashboard size={16} stroke={1.75} />}
        active={pathname === base}
        onClick={onNavigate}
      />
      <RouterNavLink
        to="/tenants/$tenantId/users"
        params={params}
        label={t('users')}
        leftSection={<IconUsers size={16} stroke={1.75} />}
        active={pathname === `${base}/users`}
        onClick={onNavigate}
      />
      <RouterNavLink
        to="/tenants/$tenantId/groups"
        params={params}
        label={t('groups')}
        leftSection={<IconUsersGroup size={16} stroke={1.75} />}
        active={pathname === `${base}/groups`}
        onClick={onNavigate}
      />
      <RouterNavLink
        to="/tenants/$tenantId/designer"
        params={params}
        label={t('designer')}
        leftSection={<IconSignature size={16} stroke={1.75} />}
        active={pathname === `${base}/designer`}
        onClick={onNavigate}
      />
      <RouterNavLink
        to="/tenants/$tenantId/rules"
        params={params}
        label={t('rules')}
        leftSection={<IconListDetails size={16} stroke={1.75} />}
        active={pathname === `${base}/rules`}
        onClick={onNavigate}
      />
      <RouterNavLink
        to="/tenants/$tenantId/campaigns"
        params={params}
        label={t('campaigns')}
        leftSection={<IconSend size={16} stroke={1.75} />}
        active={pathname === `${base}/campaigns`}
        onClick={onNavigate}
      />
      {onboardingIncomplete ? null : onboardingLink}
      <RouterNavLink
        to="/tenants/$tenantId/tester"
        params={params}
        label={t('tester')}
        leftSection={<IconFlask size={16} stroke={1.75} />}
        active={pathname === `${base}/tester`}
        onClick={onNavigate}
      />
    </Stack>
  );
}

function TenantAssignmentNav({
  assignments,
  pathname,
  onNavigate,
  partnerScoped = false,
}: {
  assignments: { tenantId: string | null; tenantSlug: string | null }[];
  pathname: string;
  onNavigate: () => void;
  partnerScoped?: boolean;
}) {
  const { t } = useTranslation('nav');
  const tenants = assignments.filter(
    (a): a is { tenantId: string; tenantSlug: string | null } => a.tenantId !== null,
  );

  if (tenants.length === 0) {
    // Partner shell already has Customers + Team links above; a second empty-state line
    // duplicated "nowhere to go". Tenant-assigned users with no rows still need a hint.
    if (partnerScoped) {
      return null;
    }

    return (
      <Text size="xs" c="dimmed" p="sm">
        {t('noTenantAssignments')}
      </Text>
    );
  }

  return (
    <Stack gap={4}>
      <NavSectionLabel>{t('yourTenants')}</NavSectionLabel>
      {tenants.map((assignment) => {
        const label = assignment.tenantSlug ?? assignment.tenantId;
        return (
          <RouterNavLink
            key={assignment.tenantId}
            to="/tenants/$tenantId"
            params={{ tenantId: assignment.tenantId }}
            search={{ tab: 'settings' }}
            label={label}
            leftSection={
              <Center
                w={20}
                h={20}
                fz={11}
                fw={600}
                c="iris.7"
                style={{
                  borderRadius: 'var(--mantine-radius-sm)',
                  backgroundColor: 'var(--mantine-color-iris-1)',
                }}
              >
                {label.slice(0, 1).toUpperCase()}
              </Center>
            }
            active={pathname.includes(assignment.tenantId)}
            onClick={onNavigate}
          />
        );
      })}
    </Stack>
  );
}

function ColorSchemeToggle() {
  const { t } = useTranslation('common');
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light');

  return (
    <Tooltip label={computed === 'dark' ? t('switchToLight') : t('switchToDark')}>
      <ActionIcon
        variant="default"
        size="lg"
        aria-label={t('toggleColorScheme')}
        onClick={() => setColorScheme(computed === 'dark' ? 'light' : 'dark')}
      >
        {computed === 'dark' ? <IconSun size={16} /> : <IconMoonStars size={16} />}
      </ActionIcon>
    </Tooltip>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  const letters = `${first}${second}` || name.slice(0, 1);
  return (letters || '?').toUpperCase();
}

function UserMenu({ displayName, email }: { displayName: string | null; email: string }) {
  const { t } = useTranslation('common');
  return (
    <Menu position="bottom-end" width={230}>
      <Menu.Target>
        <UnstyledButton aria-label={t('accountMenu')}>
          <Group gap={4} wrap="nowrap">
            <Avatar color="iris" variant="light" radius="xl" size={30}>
              {initialsOf(displayName ?? email)}
            </Avatar>
            <IconChevronDown size={14} color="var(--mantine-color-dimmed)" />
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Text size="sm" fw={600} c="var(--mantine-color-text)">
            {displayName ?? email}
          </Text>
          <Text size="xs" c="dimmed">
            {email}
          </Text>
        </Menu.Label>
        <Menu.Divider />
        <Menu.Item component="a" href="/api/auth/logout" leftSection={<IconLogout size={14} />}>
          {t('signOut')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * Shared stage for every full-screen state (sign-in, no access, API unreachable): the mark on a
 * paper card, floating over a faint iris glow.
 */
function CenteredShellScreen({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <Center
      h="100vh"
      p="xl"
      style={{
        background: 'radial-gradient(600px circle at 50% 30%, var(--portal-glow), transparent 70%)',
      }}
    >
      <Stack align="center" gap="md" w="100%" maw={440}>
        <Paper withBorder shadow="md" p="xl" radius="lg" w="100%" className="portal-enter">
          <Stack align="center" gap="sm">
            <LogoMark size={36} />
            {children}
          </Stack>
        </Paper>
        <Text size="xs" c="dimmed">
          {t('brandTagline')}
        </Text>
      </Stack>
    </Center>
  );
}

function SignInScreen() {
  const { t } = useTranslation('common');
  // Asked rather than assumed: a deployment may offer Microsoft, Google, both, or — before anybody
  // has created a client — neither, and a button that leads to a 503 is worse than no button.
  const { data: providers, isPending } = useSignInProviders();

  const microsoft = providers?.microsoft ?? false;
  const google = providers?.google ?? false;
  const neither = !isPending && !microsoft && !google;

  return (
    <CenteredShellScreen>
      <Title order={2} ta="center">
        {t('signIn')}
      </Title>

      <Text c="dimmed" ta="center" size="sm">
        {google && microsoft
          ? t('signInMicrosoftGoogle')
          : google
            ? t('signInGoogle')
            : t('signInMicrosoft')}
      </Text>

      {isPending && <Loader type="dots" size="sm" mt="sm" />}

      {microsoft && (
        <Button
          component="a"
          href="/api/auth/login?provider=microsoft"
          mt="sm"
          fullWidth
          leftSection={<IconBrandWindows size={16} />}
        >
          {t('continueMicrosoft')}
        </Button>
      )}

      {google && (
        <Button
          component="a"
          href="/api/auth/login?provider=google"
          variant={microsoft ? 'default' : 'filled'}
          mt={microsoft ? 0 : 'sm'}
          fullWidth
          leftSection={<IconBrandGoogle size={16} />}
        >
          {t('continueGoogle')}
        </Button>
      )}

      {neither && (
        <Alert color="orange" variant="light" title={t('noSignInTitle')} mt="sm">
          {t('noSignInBody')}
        </Alert>
      )}

      <LanguageSelector compact />
    </CenteredShellScreen>
  );
}

function AwaitingAccessScreen({ email }: { email: string }) {
  const { t } = useTranslation('common');
  return (
    <CenteredShellScreen>
      <Title order={3} ta="center">
        {t('accessNotGrantedTitle')}
      </Title>
      <Text c="dimmed" ta="center" size="sm">
        {t('accessNotGrantedBody', { email })}
      </Text>
      <Anchor href="/api/auth/logout" size="sm" mt="sm">
        {t('signOut')}
      </Anchor>
      <LanguageSelector compact />
    </CenteredShellScreen>
  );
}
